import * as entityLoaders from 'modules/Cube/communication/external/quotes.api.v1/ports/entityLoaders'
import * as commonEntityLoaders from 'modules/Cube/communication/external/common.api.v1/ports/entityLoaders'
import { useCallback } from 'react'
import { ApiData } from 'modules/Cube/communication/external/quotes.api.v1/adapters/quote.adapters.in'
import * as entitySaving from 'modules/Cube/communication/external/quotes.api.v1/ports/entitySaving/useSaveQuote'
import { Currency, fetchQuery } from '@sequencehq/api'
import {
  CorePortErrors,
  CubePortImplementationProp
} from 'modules/Cube/domain/cube.domain.types'
import { ApiCustomer } from '../../common.api.v1/ports/entityLoaders/useLoadSingleCustomer'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { MINUTES } from '@sequencehq/utils/dates'
import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import { v1ApiUser } from './entityLoaders/useLoadUser'
import { isDemoEnv } from 'lib/environment/environment'
import { DEMO_QUOTE } from './demoQuote'

type UseLoadQuoteEditor = () => (
  ctx: CubePortImplementationProp
) => (
  quoteId?: string
) => Promise<{ data: ApiData | null; error: CorePortErrors | null }>

interface LoaderFunctions {
  integrationsLoader: () => Promise<any>
  customerLoader: (args: { customerId: string }) => Promise<any>
  enabledCurrenciesLoader: () => Promise<any>
  userLoader: (userId: string) => Promise<v1ApiUser | undefined>
  productsLoader: (args: {
    prices: Array<{ id: string; productId: string }>
  }) => Promise<any>
  activeQuoteApprovalAutomationVersionIdLoader: () => Promise<
    string | undefined
  >
  runningQuoteApprovalAutomationExecutionIdLoader: (
    quoteId: string
  ) => Promise<{ id: string; createTime: string } | undefined>
}

const loadQuoteData = async (
  quoteData: { data: DashboardApi20240730.Quotes.Quote | null; error: any },
  loaders: LoaderFunctions
) => {
  if (!quoteData.data || quoteData.error) {
    return {
      data: null,
      error: quoteData.error || CorePortErrors.Other
    }
  }

  // Fetch global quote settings
  const quoteSettings =
    (await fetchQuery(
      apiQueryClient,
      dashboardv20240509Client.getQuoteSettings,
      undefined,
      { staleTime: 10 * MINUTES }
    )) || {}

  const [
    integrationsLoaderData,
    customerLoaderData,
    enabledCurrenciesData,
    createdByUser,
    activeQuoteApprovalAutomationVersionId,
    runningQuoteApprovalAutomationExecution
  ] = await Promise.all([
    loaders.integrationsLoader(),
    loaders.customerLoader({
      customerId: quoteData.data?.customerId ?? ''
    }),
    loaders.enabledCurrenciesLoader(),
    quoteData.data?.createdBy
      ? loaders.userLoader(quoteData.data.createdBy)
      : Promise.resolve(undefined),
    loaders.activeQuoteApprovalAutomationVersionIdLoader(),
    quoteData.data?.id
      ? loaders.runningQuoteApprovalAutomationExecutionIdLoader(
          quoteData.data.id
        )
      : Promise.resolve(undefined)
  ])

  const pricesData =
    quoteData.data?.phases
      ?.flatMap(({ prices }) => prices)
      .map(price => ({
        ...price,
        currency: quoteData.data?.currency as Currency
      })) ?? []
  const productsLoaderData = await loaders.productsLoader({
    prices: pricesData
  })

  return {
    data: {
      quote: quoteData.data,
      presentation: quoteData.data.presentation,
      prices: pricesData,
      products: productsLoaderData.products,
      customers: [customerLoaderData].filter((c): c is ApiCustomer =>
        Boolean(c)
      ),
      integrations: integrationsLoaderData.integrations,
      currencies: enabledCurrenciesData,
      createdByUser,
      quoteSettings,
      merchantBranding: quoteData.data?.merchantBranding,
      activeQuoteApprovalAutomationVersionId,
      runningQuoteApprovalAutomationExecutionId:
        runningQuoteApprovalAutomationExecution?.id,
      runningQuoteApprovalAutomationExecutionCreateTime:
        runningQuoteApprovalAutomationExecution?.createTime
    },
    error: null
  } as { data: ApiData | null; error: CorePortErrors | null }
}

export const useLoadQuoteEditor: UseLoadQuoteEditor = () => {
  const quoteLoader = entityLoaders.useLoadQuote()
  const integrationsLoader = commonEntityLoaders.useLoadIntegrations()
  const productsLoader = commonEntityLoaders.useLoadProducts()
  const customerLoader = commonEntityLoaders.useLoadSingleCustomer()
  const enabledCurrenciesLoader = commonEntityLoaders.useLoadEnabledCurrencies()
  const quoteMutator = entitySaving.useSaveQuote()
  const userLoader = entityLoaders.useLoadUser()
  const activeQuoteApprovalAutomationVersionIdLoader =
    entityLoaders.useLoadActiveQuoteApprovalAutomationVersionId()
  const runningQuoteApprovalAutomationExecutionIdLoader =
    entityLoaders.useLoadRunningQuoteApprovalAutomationExecutionId()

  const loadQuote = useCallback(
    (ctx: CubePortImplementationProp) =>
      async (
        quoteId?: string
      ): Promise<{ data: ApiData | null; error: CorePortErrors | null }> => {
        /**
         * For demo mode, we have no way of creating a quote via the permissions
         * granted to the API key. Therefore, we 'fake' the creation of a quote
         * by loading in the constant for a demo mode quote. However, we do have
         * to also handle the case of loading the demo quote by its ID, as this
         * is a follow up request made after initialization
         */
        if (isDemoEnv() && (!quoteId || quoteId == DEMO_QUOTE.id)) {
          return loadQuoteData(
            { data: DEMO_QUOTE, error: null },
            {
              integrationsLoader,
              customerLoader,
              enabledCurrenciesLoader,
              userLoader,
              productsLoader,
              activeQuoteApprovalAutomationVersionIdLoader,
              runningQuoteApprovalAutomationExecutionIdLoader
            }
          )
        }

        if (!quoteId) {
          // Check for template ID in context
          const quoteTemplateId = ctx.defaultValues?.quoteTemplateId
          if (quoteTemplateId) {
            const newQuoteRes =
              await dashboard20240730Client.postCreateQuoteFromTemplate({
                id: quoteTemplateId
              })

            if (!newQuoteRes.data || newQuoteRes.error) {
              return {
                data: null,
                error: CorePortErrors.Other
              }
            }

            const loadedNewQuote = await quoteLoader(newQuoteRes.data.id)
            return loadQuoteData(loadedNewQuote, {
              integrationsLoader,
              customerLoader,
              enabledCurrenciesLoader,
              userLoader,
              productsLoader,
              activeQuoteApprovalAutomationVersionIdLoader,
              runningQuoteApprovalAutomationExecutionIdLoader
            })
          }

          // No template ID, create a new quote
          const newQuote = await quoteMutator.create({
            customerId: ctx.defaultValues?.customerId,
            salesforceOpportunityId: ctx.defaultValues?.salesforceOpportunityId
          })

          if (!newQuote) {
            return {
              data: null,
              error: CorePortErrors.Other
            }
          }

          const loadedNewQuote = await quoteLoader(newQuote.id)
          return loadQuoteData(loadedNewQuote, {
            integrationsLoader,
            customerLoader,
            enabledCurrenciesLoader,
            userLoader,
            productsLoader,
            activeQuoteApprovalAutomationVersionIdLoader,
            runningQuoteApprovalAutomationExecutionIdLoader
          })
        }

        const loadedQuote = await quoteLoader(quoteId)
        return loadQuoteData(loadedQuote, {
          integrationsLoader,
          customerLoader,
          enabledCurrenciesLoader,
          userLoader,
          productsLoader,
          activeQuoteApprovalAutomationVersionIdLoader,
          runningQuoteApprovalAutomationExecutionIdLoader
        })
      },
    [
      quoteLoader,
      quoteMutator,
      integrationsLoader,
      customerLoader,
      enabledCurrenciesLoader,
      userLoader,
      productsLoader,
      activeQuoteApprovalAutomationVersionIdLoader,
      runningQuoteApprovalAutomationExecutionIdLoader
    ]
  )

  return loadQuote
}
